import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload as UploadIcon } from 'lucide-react';
import { uploadFileToS3 } from '../services/api';
import { LexdexLogo } from '../components/LexdexLogo';
import './Upload.css';

const Upload: React.FC = () => {
  const navigate = useNavigate();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [statusMessage, setStatusMessage] = useState('');
  const [dragActive, setDragActive] = useState(false);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setUploadStatus('idle');
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setIsUploading(true);
    setUploadStatus('idle');
    
    try {
      await uploadFileToS3(selectedFile);
      setUploadStatus('success');
      setStatusMessage(`File "${selectedFile.name}" uploaded successfully! It will be processed and added to your knowledge base shortly.`);
      setSelectedFile(null);
    } catch (error) {
      setUploadStatus('error');
      setStatusMessage(`Failed to upload "${selectedFile.name}". Please try again.`);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const file = e.dataTransfer.files?.[0];
    if (file) {
      setSelectedFile(file);
      setUploadStatus('idle');
    }
  };

  return (
    <div className="upload-page">
      <header className="upload-header">
        <button onClick={() => navigate('/')} className="back-button" aria-label="Back to chat">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
        </button>
        <div className="upload-header-content">
          <LexdexLogo className="upload-logo" showText={false} />
          <div>
            <h1 className="upload-title">Upload Documents</h1>
            <p className="upload-subtitle">Add files to your knowledge base</p>
          </div>
        </div>
      </header>

      <main className="upload-main">
        <div className="upload-container">
          {uploadStatus !== 'idle' && (
            <div className={`upload-status ${uploadStatus}`}>
              <p>{statusMessage}</p>
            </div>
          )}

          <div
            className={`file-drop-area ${dragActive ? 'drag-active' : ''}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <UploadIcon size={64} />
            <h2 className="drop-title">Drag & drop your file here</h2>
            <p className="drop-text">or</p>
            <label htmlFor="file-input" className="file-input-label">
              Browse Files
            </label>
            <input
              id="file-input"
              type="file"
              onChange={handleFileChange}
              className="file-input-hidden"
              accept=".pdf,.txt,.doc,.docx,.md"
            />
            <p className="drop-hint">Supported formats: PDF, TXT, DOC, DOCX, MD</p>
          </div>

          {selectedFile && (
            <div className="selected-file-card">
              <div className="file-details">
                <div className="file-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"></path>
                    <polyline points="13 2 13 9 20 9"></polyline>
                  </svg>
                </div>
                <div className="file-info-details">
                  <p className="file-name">{selectedFile.name}</p>
                  <p className="file-meta">
                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
              </div>
              <button
                onClick={handleUpload}
                disabled={isUploading}
                className="upload-file-button"
              >
                {isUploading ? (
                  <>
                    <span className="spinner"></span>
                    Uploading...
                  </>
                ) : (
                  <>
                    <UploadIcon size={20} />
                    Upload File
                  </>
                )}
              </button>
            </div>
          )}

          <div className="upload-info-card">
            <h3 className="info-card-title">Upload Guidelines</h3>
            <ul className="info-list">
              <li>Maximum file size: 100 MB</li>
              <li>Files are stored securely in your personal folder</li>
              <li>Processing typically takes 1-5 minutes</li>
              <li>You can query uploaded documents in the chat</li>
            </ul>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Upload;

